'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../lib/cn'

export interface GlowBottomProps {
  /**
   * Color source from theme variables
   * @default 'primary'
   */
  color?: 'primary' | 'accent' | 'muted'

  /**
   * Enable "light turning on" entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * GlowBottom - Radial gradient glow emanating from bottom center
 *
 * Creates a soft, theme-aware gradient glow effect that works in both
 * light and dark modes. Uses OKLCH relative color syntax for theme integration.
 *
 * Fixed intensity: 0.15 (both light and dark)
 *
 * @example
 * ```tsx
 * // Static
 * <GlowBottom />
 *
 * // With entrance animation
 * <GlowBottom animate />
 * ```
 */
export function GlowBottom({
  color = 'primary',
  animate = true,
  className,
}: GlowBottomProps) {
  const prefersReducedMotion = useReducedMotion()
  const colorVar = color === 'muted' ? '--muted-foreground' : `--${color}`

  const style = {
    background: `radial-gradient(
      ellipse 80% 60% at 50% 100%,
      oklch(from var(${colorVar}) calc(l - 0.1) c h / 0.15),
      transparent 70%
    )`,
  }

  // Static version
  if (!animate) {
    return (
      <div
        className={cn('absolute inset-0 z-0', className)}
        style={style}
      />
    )
  }

  // Animated version
  return (
    <motion.div
      className={cn('absolute inset-0 z-0 origin-bottom', className)}
      style={style}
      initial={{ opacity: 0, scaleY: 0.3, scaleX: 0.6 }}
      animate={{ opacity: 1, scaleY: 1, scaleX: 1 }}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
